import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './App.css';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import ScrollToTop from './components/ScrollToTop';

// Page Components
import HomePage from './pages/Home';
// import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import IndustriesPage from './pages/IndustriesPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import HospitalManagementPage from './pages/HospitalManagementPage';
import ErpSoftwarePage from './pages/ErpSoftwarePage';
import CrmSoftwarePage from './pages/CrmSoftwarePage';
import GhaziabadPage from './pages/GhaziabadPage';
import NoidaPage from './pages/NoidaPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import CustomSoftwareDevPage from './pages/CustomSoftwareDevPage';
import WebDevPage from './pages/WebDevPage';
import MobileAppDevPage from './pages/MobileAppDevPage';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  '@id': 'https://wayug.com/#organization',
  name: 'Wayug Infotech Solutions',
  legalName: 'Wayug Infotech Solutions Private Limited',
  url: 'https://wayug.com',
  logo: {
    '@type': 'ImageObject',
    url: 'https://wayug.com/logofull.png',
    width: 300,
    height: 60,
  },
  image: 'https://wayug.com/logofull.png',
  description:
    'Wayug Infotech Solutions is a premier enterprise IT services company in India specialising in custom software development, cloud migration, cybersecurity, AI automation, and managed IT services.',
  telephone: '+91-8287999236',
  email: 'wayugpl@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Connaught Place',
    addressLocality: 'New Delhi',
    addressRegion: 'Delhi',
    postalCode: '110001',
    addressCountry: 'IN',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 28.6289,
    longitude: 77.2065,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+91-8287999236',
      contactType: 'customer service',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+91-8287999236',
      contactType: 'sales',
      areaServed: 'IN',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://www.linkedin.com/company/wayug-infotech',
    'https://twitter.com/wayug',
    'https://github.com/wayug',
  ],
  numberOfEmployees: { '@type': 'QuantitativeValue', value: 50 },
  foundingDate: '2019',
  areaServed: {
    '@type': 'Country',
    name: 'India',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Enterprise IT Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cloud & DevOps', description: 'AWS, Azure, GCP cloud migration and Kubernetes orchestration for enterprises.' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cybersecurity', description: 'Zero-trust architecture, penetration testing, and 24/7 SOC-as-a-Service.' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI & Automation', description: 'Machine learning, RPA, NLP, and LLM integration for business process automation.' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Software Development', description: 'Full-stack web apps, mobile apps, microservices and API engineering.' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'IT Managed Services', description: '24/7 NOC, ITIL-aligned helpdesk, proactive monitoring and disaster recovery.' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Data & Analytics', description: 'BI dashboards, ETL pipelines, data lakes and ML pipeline deployment.' } },
    ],
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '4',
    bestRating: '5',
    worstRating: '1',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://wayug.com/#website',
  name: 'Wayug Infotech Solutions',
  url: 'https://wayug.com/',
  description: 'Enterprise IT services, custom software development and AI solutions in India.',
  publisher: { '@id': 'https://wayug.com/#organization' },
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://wayug.com/?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

function App() {
  return (
    <div className="app">
      <Helmet>
        {/* ── Primary Meta ── */}
        <title>Custom Software Development Company Delhi NCR | ERP, CRM &amp; Apps – Wayug</title>
        <meta name="description" content="Wayug Infotech is a premier custom software development company in Delhi NCR. Custom ERP for manufacturing, Mediway hospital software, CRM, mobile apps &amp; websites in Noida &amp; Ghaziabad." />
        <link rel="canonical" href="https://wayug.com/" />

        {/* ── Robots Directives ── */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />

        {/* ── Open Graph ── */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wayug Infotech Solutions" />
        <meta property="og:title" content="Custom Software Development Company Delhi NCR | Wayug Infotech" />
        <meta property="og:description" content="Custom software development company in Delhi NCR, Noida &amp; Ghaziabad. ERP for manufacturing, hospital management software, CRM, mobile apps &amp; cloud services." />
        <meta property="og:image" content="https://wayug.com/logofull.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Wayug Infotech Solutions - Custom Software Development Delhi NCR" />
        <meta property="og:url" content="https://wayug.com/" />
        <meta property="og:locale" content="en_IN" />

        {/* ── Twitter Card ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@wayug" />
        <meta name="twitter:title" content="Custom Software Development Company Delhi NCR | Wayug Infotech" />
        <meta name="twitter:description" content="Custom software, ERP, hospital management software, CRM &amp; mobile apps for businesses in Delhi NCR, Noida &amp; Ghaziabad." />
        <meta name="twitter:image" content="https://wayug.com/logofull.png" />

        {/* ── Additional SEO Meta ── */}
        <meta name="keywords" content="custom software development company Delhi NCR, software development company in Noida, software development company in Ghaziabad, ERP software for manufacturing India, hospital management software India, CRM software small business India, mobile app development company Noida, steel industry ERP India, IT company Delhi NCR, website development company Delhi" />
        <meta name="author" content="Wayug Infotech Solutions" />
        <meta name="geo.region" content="IN-DL" />
        <meta name="geo.placename" content="New Delhi" />
        <meta name="geo.position" content="28.6289;77.2065" />
        <meta name="ICBM" content="28.6289, 77.2065" />

        {/* ── Structured Data ── */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      <ScrollToTop />
      <Header />
      <main aria-label="Main content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* <Route path="/about" element={<AboutPage />} /> */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/hospital-management-software" element={<HospitalManagementPage />} />
          <Route path="/erp-software" element={<ErpSoftwarePage />} />
          <Route path="/crm-software" element={<CrmSoftwarePage />} />
          <Route path="/ghaziabad" element={<GhaziabadPage />} />
          <Route path="/noida" element={<NoidaPage />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/custom-software-development" element={<CustomSoftwareDevPage />} />
          <Route path="/web-development" element={<WebDevPage />} />
          <Route path="/mobile-app-development" element={<MobileAppDevPage />} />
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

export default App;
